<script lang="ts" setup>
import type { ProjectsDropDownSelectedProject } from '@/components/common/projects/ProjectsDropDown.vue';
import { PermissionType, ReviewPermissionType } from 'cesg-shared/dist/models';
import { ref, watch } from 'vue';
import PermissionModalView, {
  type PermissionModalAction,
  type PermissionModalEmits,
  type PermissionModalPermissions,
  type PermissionModalViewProps,
} from './PermissionModalView.vue';

export type { ProjectsDropDownSelectedProject } from '@/components/common/projects/ProjectsDropDown.vue';
export type {
  PermissionModalAction,
  PermissionModalEmits,
  PermissionModalPermissions,
  PermissionModalViewProps,
} from './PermissionModalView.vue';

export type PermissionModalProps = {
  data: PermissionModalViewProps['data'] & {
    project?: { id: string; name: string; number: string; status?: string };
    permissions?: PermissionModalPermissions;
  };
};

const props = defineProps<PermissionModalProps>();
const emit = defineEmits<PermissionModalEmits>();

const defaultPermissions: PermissionModalPermissions = {
  overview: PermissionType.DISABLED,
  budget: PermissionType.DISABLED,
  forecast: PermissionType.DISABLED,
  ata: PermissionType.DISABLED,
  invoice: PermissionType.DISABLED,
  files: PermissionType.DISABLED,
  review: ReviewPermissionType.PROHIBITED,
};

const editablePermissions = ref<PermissionModalPermissions>({
  ...(props.data.permissions ?? defaultPermissions),
});

const initialPermissions = ref<PermissionModalPermissions>({
  ...(props.data.permissions ?? defaultPermissions),
});

const selectedProjects = ref<ProjectsDropDownSelectedProject[]>(
  props.data.project
    ? [
        {
          id: props.data.project.id,
          name: props.data.project.name,
          number: props.data.project.number,
          status: props.data.project.status ?? undefined,
        },
      ]
    : [],
);

watch(
  () => props.data,
  (newData) => {
    editablePermissions.value = {
      ...((
        newData as typeof props.data & {
          permissions?: PermissionModalPermissions;
        }
      ).permissions ?? defaultPermissions),
    };
    initialPermissions.value = {
      ...((
        newData as typeof props.data & {
          permissions?: PermissionModalPermissions;
        }
      ).permissions ?? defaultPermissions),
    };
    selectedProjects.value = newData.project
      ? [
          {
            id: newData.project.id,
            name: newData.project.name,
            number: newData.project.number,
            status: newData.project.status ?? undefined,
          },
        ]
      : [];
  },
  { deep: true },
);

const onAction = (action: PermissionModalAction) => {
  switch (action.type) {
    case 'close':
      emit('action', { type: 'close' });
      return;
    case 'cancel':
      editablePermissions.value = { ...initialPermissions.value };
      emit('action', { type: 'cancel' });
      return;
    case 'save':
      emit('action', {
        type: 'save',
        permissions: editablePermissions.value,
        projects: selectedProjects.value,
      });
      return;
  }
};
</script>

<template>
  <PermissionModalView
    v-model:permissions="editablePermissions"
    v-model:selected-projects="selectedProjects"
    :data="{
      user: props.data.user,
      excludeProjectIds: props.data.excludeProjectIds,
    }"
    :disable="{ save: false }"
    @action="onAction"
  />
</template>
